/**
 * HearingBriefPanel — Hearing Intelligence
 *
 * Provides contextual preparation intelligence before a hearing and
 * post-hearing workflow prompts after one. Designed to feel like a
 * highly competent chambers assistant: calm, precise, operationally aware.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Gavel,
  MapPin,
  Clock,
  FileText,
  CaretRight,
  CheckCircle,
  WarningCircle,
  VideoCamera,
  Scales,
  User,
  ArrowSquareOut,
  CircleNotch,
  ClockCountdown,
  Files,
  ArrowsClockwise,
  CalendarCheck,
  ArrowRight,
  ChatCircleText,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchHearingBrief, type HearingBrief } from '@/lib/hearingBriefApi';
import { cn } from '@/lib/utils';

interface Props {
  hearingId: string;
  /** Called when the barrister records an outcome from the brief panel */
  onRecordOutcome?: () => void;
  /** Called when the barrister navigates to the matter */
  onOpenMatter?: (matterId: string) => void;
}

// ─── Status helpers ───────────────────────────────────────────

function hoursLabel(h: number): string {
  if (h < 0) {
    const abs = Math.abs(h);
    if (abs < 24) return `${abs} hour${abs !== 1 ? 's' : ''} ago`;
    const d = Math.floor(abs / 24);
    return `${d} day${d !== 1 ? 's' : ''} ago`;
  }
  if (h === 0) return 'now';
  if (h < 24) return `in ${h} hour${h !== 1 ? 's' : ''}`;
  if (h < 48) return 'tomorrow';
  const d = Math.floor(h / 24);
  return `in ${d} days`;
}

function preparationBadge(status: HearingBrief['intelligence']['preparationStatus']) {
  if (status === 'action_needed') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-danger/10 px-2.5 py-0.5 text-xs font-semibold text-danger ring-1 ring-danger/20"><WarningCircle className="h-3 w-3" weight="fill" />Action required</span>;
  }
  if (status === 'review_required') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-500/10 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30"><WarningCircle className="h-3 w-3" weight="fill" />Review recommended</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-success-bg px-2.5 py-0.5 text-xs font-semibold text-success ring-1 ring-success/20"><CheckCircle className="h-3 w-3" weight="fill" />Preparation current</span>;
}

function categoryLabel(cat: string): string {
  const map: Record<string, string> = {
    pleading: 'Pleading', evidence: 'Evidence', brief: 'Brief',
    opinion: 'Opinion', order: 'Order', judgment: 'Judgment',
    draft: 'Draft', fee_note: 'Fee note', contract: 'Contract',
    skeleton: 'Skeleton', authorities: 'Authorities', bundle: 'Bundle',
    other: 'Document',
  };
  return map[cat] ?? cat;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── Section wrapper ──────────────────────────────────────────

function Section({
  icon,
  title,
  children,
  className,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────

export function HearingBriefPanel({ hearingId, onRecordOutcome, onOpenMatter }: Props) {
  const navigate = useNavigate();
  const [showTimeline, setShowTimeline] = useState(false);

  const { data: brief, isLoading, error } = useQuery({
    queryKey: ['hearing-brief', hearingId],
    queryFn: () => fetchHearingBrief(hearingId),
    // Fetch once per open: the server advances the "last viewed" baseline on each
    // GET, so refetching mid-session would wipe the "new since your last visit"
    // markers the barrister is still reading. A fresh open (remount) refetches.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  if (isLoading) {
    return (
      <Card className="p-8 text-center">
        <CircleNotch className="mx-auto mb-2 h-6 w-6 animate-spin text-brand-600 dark:text-brand-400" />
        <p className="text-sm text-muted-foreground">Assembling hearing brief…</p>
      </Card>
    );
  }

  if (error || !brief) {
    return (
      <Card className="p-6 text-center">
        <WarningCircle className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Brief unavailable — check your connection and try again.</p>
      </Card>
    );
  }

  const { hearing, matter, preparation, intelligence } = brief;

  return (
    <div className="space-y-4">

      {/* ── Hearing header ── */}
      <Card className="overflow-hidden border-0 bg-[#111827] text-white shadow-lg">
        <CardContent className="p-5">

          {/* Top row: type badge + preparation status */}
          <div className="mb-4 flex items-start justify-between gap-3">
            <Badge className="border-white/20 bg-white/10 text-white/90 hover:bg-white/10">
              {hearing.type}
            </Badge>
            {preparationBadge(intelligence.preparationStatus)}
          </div>

          {/* Primary hearing identity */}
          <div className="mb-1 text-2xl font-semibold tracking-tight">
            {hearing.time}
            <span className="ml-2 text-base font-normal text-white/60">·</span>
            <span className="ml-2 text-base font-normal text-white/60">{hoursLabel(hearing.hoursUntil)}</span>
          </div>
          <div className="text-base text-white/80">{hearing.date}</div>

          {/* Location */}
          <div className="mt-4 space-y-1.5">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" weight="fill" />
              <span>
                {hearing.court}
                {hearing.courtroom && <span className="ml-1 text-white/50">· {hearing.courtroom}</span>}
              </span>
            </div>
            {hearing.judge && (
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Scales className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{hearing.judge}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Clock className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{hearing.durationLabel}</span>
            </div>
            {hearing.isVirtual && (
              <div className="flex items-center gap-2 text-sm text-white/70">
                <VideoCamera className="h-3.5 w-3.5 flex-shrink-0" weight="fill" />
                <span>Remote hearing{hearing.videoLink && ' — link available'}</span>
              </div>
            )}
          </div>

          {/* Video join link */}
          {hearing.videoLink && (
            <a
              href={hearing.videoLink}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/20"
            >
              <VideoCamera className="h-3.5 w-3.5" weight="fill" />
              Join remote hearing
              <ArrowSquareOut className="h-3 w-3" />
            </a>
          )}
        </CardContent>
      </Card>

      {/* ── Matter context ── */}
      <Card>
        <CardContent className="p-4">
          <Section icon={<Gavel className="h-3 w-3" />} title="Matter">
            <div>
              <div className="flex items-start justify-between gap-2">
                <div>
                  {matter.caseNumber && (
                    <div className="text-xs text-muted-foreground">{matter.caseNumber}</div>
                  )}
                  <div className="text-sm font-semibold text-foreground">{matter.caseName}</div>
                  {matter.practiceArea && (
                    <div className="mt-0.5 text-xs text-muted-foreground">{matter.practiceArea}</div>
                  )}
                </div>
                <button
                  onClick={() => onOpenMatter?.(matter.id) ?? navigate(`/barrister/matters/${matter.id}`)}
                  className="flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-muted-foreground transition hover:bg-muted"
                >
                  Open matter <ArrowRight className="h-3 w-3" />
                </button>
              </div>

              {/* Key parties */}
              <div className="mt-3 space-y-1 border-t border-border pt-3">
                {matter.leadBarristerName && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <User className="h-3 w-3 flex-shrink-0 text-brand-600 dark:text-brand-400" />
                    <span className="text-foreground">{matter.leadBarristerName}</span>
                    <span>· Lead counsel</span>
                  </div>
                )}
                {matter.juniorBarristerName && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <User className="h-3 w-3 flex-shrink-0" />
                    <span className="text-foreground">{matter.juniorBarristerName}</span>
                    <span>· Junior counsel</span>
                  </div>
                )}
                {matter.instructingSolicitorName && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <User className="h-3 w-3 flex-shrink-0" />
                    <span>{matter.instructingSolicitorName}</span>
                  </div>
                )}
                {matter.layClientName && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <User className="h-3 w-3 flex-shrink-0" />
                    <span>{matter.layClientName}</span>
                    <span>· Client</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {matter.description && (
                <p className="mt-3 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground">
                  {matter.description}
                </p>
              )}
            </div>
          </Section>
        </CardContent>
      </Card>

      {/* ── Preparation intelligence ── */}
      {intelligence.isUpcoming && (
        <Card>
          <CardContent className="p-4 space-y-4">

            {/* Since your last visit */}
            {(() => {
              const s = preparation.sinceLastVisit;
              if (!s) return null;
              const total = s.newDocuments + s.newCorrespondence + s.newActivity;
              if (total === 0) {
                return (
                  <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    <CheckCircle className="h-3.5 w-3.5 text-success" weight="fill" />
                    Nothing new since your last visit ({timeAgo(s.previousVisitAt)}).
                  </div>
                );
              }
              const parts = [
                s.newDocuments ? `${s.newDocuments} document${s.newDocuments !== 1 ? 's' : ''}` : null,
                s.newCorrespondence ? `${s.newCorrespondence} message${s.newCorrespondence !== 1 ? 's' : ''}` : null,
                s.newActivity ? `${s.newActivity} update${s.newActivity !== 1 ? 's' : ''}` : null,
              ].filter(Boolean).join(' · ');
              return (
                <div className="rounded-md border border-brand-200 bg-brand-50 px-3 py-2">
                  <div className="flex items-center gap-2 text-xs font-semibold text-brand-800 dark:text-brand-200">
                    <WarningCircle className="h-3.5 w-3.5" weight="fill" />
                    New since your last visit ({timeAgo(s.previousVisitAt)})
                  </div>
                  <div className="mt-0.5 text-xs text-brand-700 dark:text-brand-300">{parts}</div>
                </div>
              );
            })()}

            {/* Estimated review time */}
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ClockCountdown className="h-3.5 w-3.5" />
                Estimated preparation review
              </div>
              <div className="text-xs font-semibold text-foreground">
                {preparation.estimatedReviewMinutes} min
              </div>
            </div>

            {/* Recent documents */}
            {preparation.recentDocuments.length > 0 && (
              <Section icon={<Files className="h-3 w-3" />} title={`${preparation.recentDocuments.length} document${preparation.recentDocuments.length !== 1 ? 's' : ''} recently added`}>
                <ul className="space-y-1.5">
                  {preparation.recentDocuments.slice(0, 5).map((doc) => (
                    <li key={doc.id} className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <FileText className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        <span className={`truncate text-xs ${doc.isNew ? 'font-semibold text-foreground' : 'text-foreground'}`}>{doc.name}</span>
                        {doc.isNew && <span className="shrink-0 rounded-full bg-brand-100 px-1.5 py-0 text-[9px] font-semibold text-brand-700 dark:text-brand-300">New</span>}
                        <span className="shrink-0 text-[10px] text-muted-foreground">{categoryLabel(doc.category)}</span>
                      </div>
                      <span className="shrink-0 text-[10px] text-muted-foreground">{timeAgo(doc.uploadedAt)}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Latest correspondence — so the barrister never has to leave for Outlook */}
            {preparation.recentCorrespondence.length > 0 && (
              <Section icon={<ChatCircleText className="h-3 w-3" />} title="Latest correspondence">
                <ul className="space-y-1.5">
                  {preparation.recentCorrespondence.slice(0, 5).map((c) => (
                    <li key={`${c.source}-${c.id}`} className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-start gap-2">
                        {c.source === 'email'
                          ? <EnvelopeSimple className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                          : <ChatCircleText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={cn('truncate text-xs', c.unread ? 'font-semibold text-foreground' : 'text-foreground')}>
                              {c.subject}
                            </span>
                            {c.unread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600" />}
                            {c.isNew && <span className="shrink-0 rounded-full bg-brand-100 px-1.5 py-0 text-[9px] font-semibold text-brand-700 dark:text-brand-300">New</span>}
                          </div>
                          {(c.counterpart || c.preview) && (
                            <p className="truncate text-[10px] text-muted-foreground">
                              {c.counterpart ? `${c.counterpart}: ` : ''}{c.preview ?? ''}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="shrink-0 text-[10px] text-muted-foreground">{timeAgo(c.at)}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Recent matter activity */}
            {preparation.recentActivity.length > 0 && (
              <Section icon={<ArrowsClockwise className="h-3 w-3" />} title="Recent activity">
                <ul className="space-y-1.5">
                  {preparation.recentActivity.slice(0, 4).map((a, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span className="truncate">{a.description}</span>
                      <span className="shrink-0 text-[10px]">{timeAgo(a.timestamp)}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Previous hearing */}
            {preparation.previousHearing && (
              <Section icon={<CalendarCheck className="h-3 w-3" />} title="Previous hearing">
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-foreground">{preparation.previousHearing.type}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{preparation.previousHearing.date}</span>
                    <Badge variant={preparation.previousHearing.status === 'completed' ? 'success' : 'warning'} className="ml-auto text-[10px]">
                      {preparation.previousHearing.status}
                    </Badge>
                  </div>
                  {preparation.previousHearing.outcome && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {preparation.previousHearing.outcome}
                    </p>
                  )}
                </div>
              </Section>
            )}

            {/* No updates state */}
            {preparation.recentDocuments.length === 0 && preparation.recentActivity.length === 0 && preparation.recentCorrespondence.length === 0 && !preparation.previousHearing && (
              <div className="rounded-md border border-dashed border-border px-4 py-5 text-center">
                <CheckCircle className="mx-auto mb-1.5 h-5 w-5 text-success" weight="fill" />
                <p className="text-xs text-muted-foreground">No updates since last review. Matter is stable.</p>
              </div>
            )}

          </CardContent>
        </Card>
      )}

      {/* ── Post-hearing workflow ── */}
      {intelligence.isPast && (
        <Card className={cn(intelligence.requiresOutcome && 'border-amber-200 dark:border-amber-500/30 bg-amber-50/30 dark:bg-amber-500/10')}>
          <CardContent className="p-4">
            {intelligence.requiresOutcome ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <WarningCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" weight="fill" />
                  <span className="text-sm font-medium text-foreground">Outcome not yet recorded</span>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  This hearing has passed. Recording the outcome keeps the matter timeline accurate and generates the activity record.
                </p>
                {/* No handler = a read-only viewer (pupil): the server refuses the outcome, so
                    state the fact rather than drawing a button that 403s. */}
                {onRecordOutcome ? (
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-full"
                    onClick={onRecordOutcome}
                  >
                    Record outcome
                  </Button>
                ) : (
                  <p className="text-xs italic text-muted-foreground">
                    The barrister on this matter records the outcome.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-success" weight="fill" />
                  <span className="text-sm font-medium text-foreground">Outcome recorded</span>
                </div>
                {hearing.outcome && (
                  <p className="rounded-md bg-muted/40 px-3 py-2 text-xs leading-relaxed text-foreground">
                    {hearing.outcome}
                  </p>
                )}
                {hearing.notes && (
                  <p className="text-xs text-muted-foreground">{hearing.notes}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Hearing timeline ── */}
      {preparation.allCaseHearings.length > 1 && (
        <Card>
          <CardContent className="p-4">
            <button
              className="flex w-full items-center justify-between text-left"
              onClick={() => setShowTimeline((v) => !v)}
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground">
                <CalendarCheck className="h-3 w-3" />
                Hearing history ({preparation.allCaseHearings.length})
              </div>
              <CaretRight className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', showTimeline && 'rotate-90')} />
            </button>
            {showTimeline && (
              <ul className="mt-3 space-y-2 border-t border-border pt-3">
                {preparation.allCaseHearings.map((h) => (
                  <li
                    key={h.id}
                    className={cn(
                      'flex items-start gap-3 rounded-md px-2 py-1.5 text-xs',
                      h.isCurrent ? 'bg-brand-50 ring-1 ring-brand-200' : '',
                    )}
                  >
                    <div className={cn(
                      'mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full',
                      h.status === 'completed' ? 'bg-success' :
                      h.status === 'adjourned' ? 'bg-amber-500' :
                      h.isCurrent ? 'bg-brand-600' : 'bg-muted-foreground',
                    )} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={cn('font-medium', h.isCurrent ? 'text-brand-700 dark:text-brand-300' : 'text-foreground')}>
                          {h.date}
                        </span>
                        <span className="text-muted-foreground">· {h.type}</span>
                        {h.isCurrent && (
                          <span className="rounded-full bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 dark:text-brand-300">This hearing</span>
                        )}
                      </div>
                      {h.outcome && (
                        <p className="mt-0.5 truncate text-muted-foreground">{h.outcome}</p>
                      )}
                    </div>
                    <Badge variant={h.status === 'completed' ? 'success' : 'default'} className="shrink-0 text-[10px]">
                      {h.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
